/**
 * Document Version History - Google Docs-aligned prototype
 *
 * Access: http://flownotes.localhost/?test=document-version-history
 *
 * Day headers + same-minute time clusters (collapsed summary), nested edits,
 * OS-aware mod+click compare. Webapp light/dark tokens (maater-design-ui).
 * Bet: docs/product/bets/2026-06-04-document-version-history-view.md
 */

import { useMemo, useState, type MouseEvent } from 'react'
import {
  ChevronDown,
  ChevronRight,
  Clock,
  MoreVertical,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { detectShortcutOs, modKeyLabel } from '@/lib/keyboard-platform'
import { isModClick } from '@/lib/open-intent'
import { cn } from '@/lib/utils'

function ModCompareHint({ className }: { className?: string }) {
  const mod = modKeyLabel(detectShortcutOs())
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-xs text-muted-foreground select-none',
        className
      )}
    >
      <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded-sm border border-border/50 bg-muted/50 px-1 font-sans text-[11px] leading-none text-foreground/80">
        {mod}
      </kbd>
      <span aria-hidden className="text-muted-foreground/70">
        +
      </span>
      <span>click to compare</span>
    </span>
  )
}

type GranularEdit = {
  id: string
  timeLabel: string
  actor: string
  badge: string
  subtitle: string
  previewBody: string
}

type VersionParent = {
  id: string
  kind: 'named' | 'session'
  timeLabel: string
  actor: string
  /** Shown as primary title for named versions */
  title?: string
  badge?: string
  subtitle?: string
  isCurrent?: boolean
  previewBody: string
  children: GranularEdit[]
  defaultExpanded?: boolean
}

type DayGroup = {
  dateKey: string
  dateLabel: string
  defaultExpanded: boolean
  parents: VersionParent[]
}

const FIXTURE_DAYS: DayGroup[] = [
  {
    dateKey: '2026-07-22',
    dateLabel: 'Jul 22, 2026',
    defaultExpanded: true,
    parents: [
      {
        id: 'ver-verified-1133',
        kind: 'named',
        timeLabel: '11:33 PM',
        actor: 'Rowan',
        title: 'Mark verified after pilot SOP pass',
        badge: 'Verified',
        isCurrent: true,
        previewBody: 'Verified milestone body at 11:33 PM (fixture cluster).',
        defaultExpanded: false,
        children: [],
      },
      {
        id: 'ver-ai-1133',
        kind: 'named',
        timeLabel: '11:33 PM',
        actor: 'AI',
        title: 'AI apply-edit from chat',
        badge: 'AI Edit',
        previewBody: 'AI edit body at 11:33 PM (fixture cluster).',
        defaultExpanded: false,
        children: [],
      },
      {
        id: 'ver-review-1133',
        kind: 'named',
        timeLabel: '11:33 PM',
        actor: 'Rowan',
        title: 'Please re-check emoji SOP examples',
        badge: 'Review requested',
        previewBody: 'Review-requested note at 11:33 PM (fixture cluster).',
        defaultExpanded: false,
        children: [],
      },
      {
        id: 'ver-named-1133a',
        kind: 'named',
        timeLabel: '11:33 PM',
        actor: 'System',
        title: 'Named snapshot before verify',
        badge: 'Named version',
        previewBody: 'Named version body at 11:33 PM (fixture cluster).',
        defaultExpanded: false,
        children: [],
      },
      {
        id: 'ver-session-1133',
        kind: 'session',
        timeLabel: '11:33 PM',
        actor: 'Rowan',
        badge: 'Session',
        subtitle: '2 edits',
        previewBody: 'Session wrapper at 11:33 PM (fixture cluster).',
        defaultExpanded: false,
        children: [
          {
            id: 'edit-manual-1133',
            timeLabel: '11:33 PM',
            actor: 'Rowan',
            badge: 'Manual save',
            subtitle: '+40 chars',
            previewBody: 'Manual save in 11:33 PM cluster.',
          },
        ],
      },
      {
        id: 'ver-named-mcp',
        kind: 'named',
        timeLabel: '1:07 PM',
        actor: 'System',
        title: 'Local MCP QA: after append on Would You Rather pilot feedback',
        badge: 'Named version',
        previewBody:
          '## Would You Rather Interactive Game Builder\n\n**Summary**\nPilot feedback after MCP append QA.\n\nNamed snapshot body at 1:07 PM (fixture).',
        defaultExpanded: true,
        children: [
          {
            id: 'edit-manual-108',
            timeLabel: '1:08 PM',
            actor: 'Rowan',
            badge: 'Manual save',
            subtitle: '+69 chars',
            previewBody:
              '## Would You Rather Interactive Game Builder\n\n**Summary**\nPilot feedback after MCP append QA.\n\n(+69 chars from manual save at 1:08 PM - fixture)',
          },
        ],
      },
      {
        id: 'ver-session-1240',
        kind: 'session',
        timeLabel: '12:40 PM',
        actor: 'Rowan',
        badge: 'Session',
        subtitle: '3 edits',
        previewBody:
          '## Would You Rather Interactive Game Builder\n\nEarlier session snapshot (fixture) at 12:40 PM.',
        defaultExpanded: false,
        children: [
          {
            id: 'edit-auto-1240',
            timeLabel: '12:40 PM',
            actor: 'Rowan',
            badge: 'Autosave',
            subtitle: '+12 chars',
            previewBody: 'Autosave body fixture 12:40 PM.',
          },
          {
            id: 'edit-mcp-1235',
            timeLabel: '12:35 PM',
            actor: 'Rowan',
            badge: 'MCP',
            subtitle: '+220 chars',
            previewBody: 'MCP append body fixture 12:35 PM.',
          },
          {
            id: 'edit-chat-1230',
            timeLabel: '12:30 PM',
            actor: 'Rowan',
            badge: 'Chat edit',
            subtitle: '+48 chars',
            previewBody: 'Chat edit body fixture 12:30 PM.',
          },
        ],
      },
    ],
  },
  {
    dateKey: '2026-07-19',
    dateLabel: 'Jul 19, 2026',
    defaultExpanded: false,
    parents: [
      {
        id: 'ver-verified',
        kind: 'named',
        timeLabel: '4:02 PM',
        actor: 'Travis',
        title: 'Verified for pilot review',
        badge: 'Verified',
        previewBody: 'Verified milestone body fixture (Jul 19).',
        defaultExpanded: false,
        children: [
          {
            id: 'edit-jul19',
            timeLabel: '3:55 PM',
            actor: 'Rowan',
            badge: 'Manual save',
            subtitle: '+ prof',
            previewBody: 'Pre-verify edit fixture.',
          },
        ],
      },
    ],
  },
]

function findParent(id: string): VersionParent | null {
  for (const day of FIXTURE_DAYS) {
    const p = day.parents.find((x) => x.id === id)
    if (p) return p
  }
  return null
}

function findEdit(id: string): { edit: GranularEdit; parent: VersionParent } | null {
  for (const day of FIXTURE_DAYS) {
    for (const parent of day.parents) {
      const edit = parent.children.find((c) => c.id === id)
      if (edit) return { edit, parent }
    }
  }
  return null
}

function previewForSelection(selectedIds: string[]): { label: string; body: string } {
  if (selectedIds.length === 0) {
    const tip = FIXTURE_DAYS[0]?.parents[0]
    return {
      label: tip ? `Current version · ${tip.timeLabel}` : 'No version selected',
      body: tip?.previewBody ?? '',
    }
  }
  if (selectedIds.length === 1) {
    const id = selectedIds[0]
    const parent = findParent(id)
    if (parent) {
      return {
        label: parent.isCurrent
          ? `Current version · ${parent.timeLabel}`
          : `Viewing version · ${parent.timeLabel}`,
        body: parent.previewBody,
      }
    }
    const nested = findEdit(id)
    if (nested) {
      return {
        label: `Viewing edit · ${nested.edit.timeLabel}`,
        body: nested.edit.previewBody,
      }
    }
  }
  const a = selectedIds[0]
  const b = selectedIds[1]
  const labelA = findParent(a)?.timeLabel ?? findEdit(a)?.edit.timeLabel ?? a
  const labelB = findParent(b)?.timeLabel ?? findEdit(b)?.edit.timeLabel ?? b
  const bodyA = findParent(a)?.previewBody ?? findEdit(a)?.edit.previewBody ?? ''
  const bodyB = findParent(b)?.previewBody ?? findEdit(b)?.edit.previewBody ?? ''
  return {
    label: `Compare · ${labelA} vs ${labelB}`,
    body: `--- A (${labelA}) ---\n${bodyA}\n\n--- B (${labelB}) ---\n${bodyB}`,
  }
}

function MetaBadge({ children, emphasis }: { children: string; emphasis?: boolean }) {
  return (
    <Badge
      variant={emphasis ? 'secondary' : 'outline'}
      className={cn(
        'h-4 px-1.5 text-[10px] font-normal border-border whitespace-nowrap shrink-0',
        emphasis && 'font-semibold',
      )}
    >
      {children}
    </Badge>
  )
}

/** Shared row chrome: chevron gutter + time + actor + badge + subtitle. */
function HistoryRow({
  id,
  depth,
  timeLabel,
  actor,
  title,
  badge,
  badgeEmphasis,
  subtitle,
  isCurrent,
  selected,
  compareSelected,
  expanded,
  hasChildren,
  onToggleExpand,
  onActivate,
  showMenu,
  hideTime,
}: {
  id: string
  depth: 0 | 1
  timeLabel: string
  actor: string
  title?: string
  badge?: string
  badgeEmphasis?: boolean
  subtitle?: string
  isCurrent?: boolean
  selected: boolean
  compareSelected: boolean
  expanded?: boolean
  hasChildren?: boolean
  onToggleExpand?: () => void
  onActivate: (e: MouseEvent) => void
  showMenu?: boolean
  hideTime?: boolean
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`proto-version-history-row-${id}`}
      onMouseDown={(e) => {
        if (isModClick(e)) e.preventDefault()
      }}
      onClick={onActivate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onActivate(e as unknown as MouseEvent)
        }
      }}
      className={cn(
        'group flex items-start gap-1 rounded-sm px-1.5 py-2 text-sm text-left w-full transition-colors select-none',
        'hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        selected && 'bg-muted/50',
        compareSelected && !selected && 'bg-primary/5 ring-1 ring-primary/25',
        depth === 1 && 'ml-0',
      )}
    >
      <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
        {hasChildren ? (
          <button
            type="button"
            className="p-0.5 rounded hover:bg-muted text-muted-foreground"
            aria-label={expanded ? 'Collapse edits' : 'Expand edits'}
            onClick={(e) => {
              e.stopPropagation()
              onToggleExpand?.()
            }}
          >
            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        ) : (
          <span className="w-3.5" aria-hidden />
        )}
      </div>

      <div className={cn('flex-1 min-w-0', depth === 1 && 'pl-0')}>
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          {!hideTime ? (
            <span className="text-muted-foreground tabular-nums shrink-0">{timeLabel}</span>
          ) : null}
          <span className="text-foreground/80 truncate">{actor}</span>
          {badge ? <MetaBadge emphasis={badgeEmphasis}>{badge}</MetaBadge> : null}
          {isCurrent ? (
            <span className="text-xs text-muted-foreground whitespace-nowrap">Current version</span>
          ) : null}
          {showMenu ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 ml-auto opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Version actions"
                >
                  <MoreVertical className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem
                  onClick={() => toast.message('Restore this version', { description: 'Production next - stub in prototype.' })}
                >
                  Restore this version
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => toast.message('Name this version', { description: 'Production next - stub in prototype.' })}
                >
                  Name this version
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
        {title ? (
          <p className="text-xs text-foreground/90 mt-0.5 line-clamp-2">{title}</p>
        ) : null}
        {subtitle ? (
          <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
        ) : null}
      </div>
    </div>
  )
}

function clusterParentsByTimeLabel(parents: VersionParent[]) {
  const clusters: { timeLabel: string; parents: VersionParent[] }[] = []
  for (const parent of parents) {
    const last = clusters[clusters.length - 1]
    if (last && last.timeLabel === parent.timeLabel) {
      last.parents.push(parent)
    } else {
      clusters.push({ timeLabel: parent.timeLabel, parents: [parent] })
    }
  }
  return clusters
}

function VersionHistoryPanel({
  onClose,
  selectedIds,
  setSelectedIds,
  expandedParents,
  setExpandedParents,
  expandedDays,
  setExpandedDays,
}: {
  onClose: () => void
  selectedIds: string[]
  setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>
  expandedParents: Set<string>
  setExpandedParents: React.Dispatch<React.SetStateAction<Set<string>>>
  expandedDays: Set<string>
  setExpandedDays: React.Dispatch<React.SetStateAction<Set<string>>>
}) {
  const [expandedTimeClusters, setExpandedTimeClusters] = useState<Set<string>>(new Set())

  const toggleDay = (dateKey: string) => {
    setExpandedDays((prev) => {
      const next = new Set(prev)
      if (next.has(dateKey)) next.delete(dateKey)
      else next.add(dateKey)
      return next
    })
  }

  const toggleParent = (id: string) => {
    setExpandedParents((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleTimeCluster = (id: string) => {
    setExpandedTimeClusters((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const activate = (id: string, e: MouseEvent) => {
    if (isModClick(e)) {
      e.preventDefault()
      setSelectedIds((prev) => {
        if (prev.includes(id)) return prev.filter((x) => x !== id)
        if (prev.length === 0) return [id]
        if (prev.length === 1) return prev[0] === id ? prev : [prev[0], id]
        return [prev[0], id]
      })
      return
    }
    setSelectedIds([id])
  }

  const canCompare = selectedIds.length === 2

  return (
    <div
      className="flex flex-col h-full bg-background border-l border-border"
      data-testid="proto-document-version-history-panel"
    >
      <div className="px-3 py-2.5 border-b border-border/60 flex items-center justify-between gap-2 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground truncate">
            Version history
          </h2>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {selectedIds.length === 1 ? <ModCompareHint className="px-1 whitespace-nowrap" /> : null}
          {canCompare ? (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => toast.success('Compare selected', { description: 'Fixture compare shown in preview.' })}
            >
              Compare selected
            </Button>
          ) : null}
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-4 select-none">
        {FIXTURE_DAYS.map((day) => {
          const dayOpen = expandedDays.has(day.dateKey)
          const clusters = clusterParentsByTimeLabel(day.parents)
          return (
            <div key={day.dateKey} className="space-y-2">
              <button
                type="button"
                onClick={() => toggleDay(day.dateKey)}
                className="flex items-center gap-1 w-full text-left px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
              >
                {dayOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                {day.dateLabel}
              </button>
              {dayOpen ? (
                <div className="space-y-2.5">
                  {clusters.map((cluster) => {
                    const clusterId = `${day.dateKey}-${cluster.timeLabel}`
                    const multi = cluster.parents.length > 1
                    const clusterOpen = !multi || expandedTimeClusters.has(clusterId)
                    const summaryBadges = [
                      ...new Set(cluster.parents.map((p) => p.badge).filter(Boolean)),
                    ].slice(0, 3) as string[]
                    const latestNote = cluster.parents.find((p) => p.title)?.title
                    return (
                      <div
                        key={clusterId}
                        className="rounded-md bg-muted/15 px-0.5 py-1"
                        data-testid={`proto-version-history-time-${clusterId}`}
                      >
                        {multi ? (
                          <button
                            type="button"
                            onClick={() => toggleTimeCluster(clusterId)}
                            className="flex w-full items-start gap-1.5 rounded-sm px-1.5 py-2 text-left hover:bg-muted/40"
                            aria-expanded={clusterOpen}
                          >
                            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground">
                              {clusterOpen ? (
                                <ChevronDown className="h-3.5 w-3.5" />
                              ) : (
                                <ChevronRight className="h-3.5 w-3.5" />
                              )}
                            </span>
                            <div className="min-w-0 flex-1 space-y-1">
                              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                <span className="text-sm font-medium tabular-nums text-foreground/90">
                                  {cluster.timeLabel}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {cluster.parents.length} events
                                </span>
                              </div>
                              {!clusterOpen ? (
                                <>
                                  <div className="flex flex-wrap gap-1">
                                    {summaryBadges.map((label) => (
                                      <Badge
                                        key={`${clusterId}-${label}`}
                                        variant="outline"
                                        className="h-4 px-1.5 text-[10px] font-normal border-border/60 text-muted-foreground"
                                      >
                                        {label}
                                      </Badge>
                                    ))}
                                  </div>
                                  {latestNote ? (
                                    <p className="text-xs text-muted-foreground line-clamp-2">{latestNote}</p>
                                  ) : null}
                                </>
                              ) : null}
                            </div>
                          </button>
                        ) : (
                          <div className="flex items-center gap-1.5 px-1.5 pt-1 pb-0.5">
                            <span className="w-5 shrink-0" aria-hidden />
                            <span className="text-sm font-medium tabular-nums text-foreground/90">
                              {cluster.timeLabel}
                            </span>
                          </div>
                        )}
                        {clusterOpen
                          ? cluster.parents.map((parent) => {
                              const open = expandedParents.has(parent.id)
                              const selected =
                                selectedIds.length === 1 && selectedIds[0] === parent.id
                              const compareSelected =
                                selectedIds.length === 2 && selectedIds.includes(parent.id)
                              return (
                                <div key={parent.id} className="space-y-0.5">
                                  <HistoryRow
                                    id={parent.id}
                                    depth={0}
                                    timeLabel={parent.timeLabel}
                                    actor={parent.actor}
                                    title={parent.title}
                                    badge={parent.badge}
                                    badgeEmphasis={parent.kind === 'named'}
                                    subtitle={parent.subtitle}
                                    isCurrent={parent.isCurrent}
                                    selected={selected}
                                    compareSelected={compareSelected}
                                    expanded={open}
                                    hasChildren={parent.children.length > 0}
                                    onToggleExpand={() => toggleParent(parent.id)}
                                    onActivate={(e) => activate(parent.id, e)}
                                    showMenu
                                    hideTime
                                  />
                                  {open
                                    ? parent.children.map((child) => {
                                        const childSelected =
                                          selectedIds.length === 1 && selectedIds[0] === child.id
                                        const childCompare =
                                          selectedIds.length === 2 &&
                                          selectedIds.includes(child.id)
                                        return (
                                          <div key={child.id} className="pl-5">
                                            <HistoryRow
                                              id={child.id}
                                              depth={1}
                                              timeLabel={child.timeLabel}
                                              actor={child.actor}
                                              badge={child.badge}
                                              subtitle={child.subtitle}
                                              selected={childSelected}
                                              compareSelected={childCompare}
                                              onActivate={(e) => activate(child.id, e)}
                                              hideTime
                                            />
                                          </div>
                                        )
                                      })
                                    : null}
                                </div>
                              )
                            })
                          : null}
                      </div>
                    )
                  })}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>

      <div className="sticky bottom-0 shrink-0 bg-background/95 backdrop-blur-sm">
        <div className="mx-3 border-t border-border/40" aria-hidden />
        <div className="flex items-center px-3 py-2">
          <ModCompareHint />
        </div>
      </div>
    </div>
  )
}

export function DocumentVersionHistoryTest() {
  const [panelOpen, setPanelOpen] = useState(true)
  const [selectedIds, setSelectedIds] = useState<string[]>(['ver-verified-1133'])
  const [expandedParents, setExpandedParents] = useState<Set<string>>(() => {
    const initial = new Set<string>()
    for (const day of FIXTURE_DAYS) {
      for (const p of day.parents) {
        if (p.defaultExpanded) initial.add(p.id)
      }
    }
    return initial
  })
  const [expandedDays, setExpandedDays] = useState<Set<string>>(() => {
    const initial = new Set<string>()
    for (const day of FIXTURE_DAYS) {
      if (day.defaultExpanded) initial.add(day.dateKey)
    }
    return initial
  })

  const preview = useMemo(() => previewForSelection(selectedIds), [selectedIds])

  return (
    <div className="h-screen flex flex-col bg-background text-foreground" data-testid="proto-document-version-history">
      <header className="h-11 border-b border-border px-4 flex items-center justify-between shrink-0">
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">Document Version History</p>
          <p className="text-[11px] text-muted-foreground truncate">
            Google Docs-aligned prototype · bet 2026-06-04
          </p>
        </div>
        <a href="/?test=index" className="text-xs text-muted-foreground hover:text-foreground shrink-0">
          Prototype index
        </a>
      </header>

      <div className="flex-1 min-h-0 flex">
        <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
          <div className="h-10 border-b border-border px-3 flex items-center justify-between gap-2 shrink-0">
            <span className="text-sm truncate">Would You Rather Interactive Game Builder</span>
            <Button
              variant={panelOpen ? 'secondary' : 'outline'}
              size="sm"
              className="h-7 text-xs shrink-0"
              onClick={() => setPanelOpen((v) => !v)}
            >
              <Clock className="h-3.5 w-3.5 mr-1" />
              History
            </Button>
          </div>
          <div className="flex-1 overflow-auto p-6">
            <p className="text-xs text-muted-foreground mb-3">{preview.label}</p>
            <pre className="text-sm whitespace-pre-wrap font-sans text-foreground/90 leading-relaxed max-w-2xl">
              {preview.body}
            </pre>
          </div>
        </main>

        {panelOpen ? (
          <aside className="w-[320px] max-w-[40vw] shrink-0 min-h-0">
            <VersionHistoryPanel
              onClose={() => setPanelOpen(false)}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              expandedParents={expandedParents}
              setExpandedParents={setExpandedParents}
              expandedDays={expandedDays}
              setExpandedDays={setExpandedDays}
            />
          </aside>
        ) : null}
      </div>
    </div>
  )
}
